import { InputHTMLAttributes } from "react";
import { InputLabelProps } from "@mui/material/InputLabel";
import { SxProps, Theme } from "@mui/material/styles";
import { GomakeOutlinedFieldClassName } from "./constants";
import { OutlinedFieldDirection } from "./use-outlined-field-theme";

const OUTLINED_PADDING_INLINE = 14;
const OUTLINED_PADDING_BLOCK = 8.5;
const OUTLINED_MULTILINE_PADDING_BLOCK = 12;
const OUTLINED_INPUT_HEIGHT = 40;
const AUTOCOMPLETE_ICON_GUTTER = 32;

const getInputPaddingSx = (
  isAutocomplete = false
): Record<string, string> => {
  if (!isAutocomplete) {
    return {
      paddingBlock: `${OUTLINED_PADDING_BLOCK}px !important`,
      paddingInlineStart: `${OUTLINED_PADDING_INLINE}px !important`,
      paddingInlineEnd: `${OUTLINED_PADDING_INLINE}px !important`,
    };
  }

  // Gutter always on inline-end (arrow side: left in RTL, right in LTR).
  // Text starts at inline-start with normal padding (first red line in RTL).
  return {
    paddingBlock: `${OUTLINED_PADDING_BLOCK}px !important`,
    paddingInlineStart: `${OUTLINED_PADDING_INLINE}px !important`,
    paddingInlineEnd: `${
      OUTLINED_PADDING_INLINE + AUTOCOMPLETE_ICON_GUTTER
    }px !important`,
  };
};

const getPlaceholderResetSx = (direction: OutlinedFieldDirection) => {
  const isRTL = direction === "rtl";

  return {
    textAlign: isRTL ? ("right" as const) : ("left" as const),
    direction,
    display: "block !important",
    padding: "0 !important",
    margin: "0 !important",
    lineHeight: "inherit !important",
    fontSize: "inherit !important",
    fontWeight: "inherit !important",
    opacity: "0.65 !important",
  };
};

const getLabelDirectionSx = (isRTL: boolean) =>
  isRTL
    ? {
        right: `${OUTLINED_PADDING_INLINE}px !important`,
        left: "auto !important",
        transformOrigin: "top right !important",
        textAlign: "right" as const,
      }
    : {
        left: `${OUTLINED_PADDING_INLINE}px !important`,
        right: "auto !important",
        transformOrigin: "top left !important",
        textAlign: "left" as const,
      };

const getOutlinedFieldDirectionSx = (
  direction: OutlinedFieldDirection,
  isAutocomplete = false
): SxProps<Theme> => {
  const isRTL = direction === "rtl";
  const inputPadding = getInputPaddingSx(isAutocomplete);
  const placeholderResetSx = getPlaceholderResetSx(direction);
  const inputLabelDirectionSx = getLabelDirectionSx(isRTL);

  return {
    direction,
    "& .MuiOutlinedInput-root:not(.MuiInputBase-multiline)": {
      direction,
      minHeight: `${OUTLINED_INPUT_HEIGHT}px`,
      height: `${OUTLINED_INPUT_HEIGHT}px`,
      padding: "0 !important",
      boxSizing: "border-box",
    },
    "& .MuiOutlinedInput-root.MuiInputBase-multiline": {
      direction,
      padding: `${OUTLINED_MULTILINE_PADDING_BLOCK}px ${OUTLINED_PADDING_INLINE}px !important`,
      alignItems: "flex-start",
      boxSizing: "border-box",
    },
    "& .MuiOutlinedInput-input, & textarea.MuiOutlinedInput-input, & .MuiOutlinedInput-inputMultiline":
      {
        direction,
        textAlign: isRTL ? "right" : "left",
        unicodeBidi: "plaintext",
        boxSizing: "border-box",
        lineHeight: "1.4375em",
        height: "auto",
        ...inputPadding,
      },
    "& .MuiAutocomplete-input": {
      direction,
      textAlign: isRTL ? "right" : "left",
      unicodeBidi: "plaintext",
      boxSizing: "border-box",
      lineHeight: "1.4375em",
      ...inputPadding,
    },
    "& .MuiOutlinedInput-root.MuiInputBase-multiline .MuiOutlinedInput-inputMultiline, & .MuiOutlinedInput-root.MuiInputBase-multiline textarea.MuiOutlinedInput-input":
      {
        padding: "0 !important",
        minHeight: "1.4375em",
      },
    "& .MuiInputBase-input": {
      direction,
      textAlign: isRTL ? "right" : "left",
      unicodeBidi: "plaintext",
      lineHeight: "1.4375em",
    },
    "& .MuiInputBase-input::placeholder, & input::placeholder, & textarea::placeholder":
      placeholderResetSx,
    "& .MuiInputBase-input::-webkit-input-placeholder, & input::-webkit-input-placeholder, & textarea::-webkit-input-placeholder":
      placeholderResetSx,
    "& .MuiInputBase-input::-moz-placeholder, & input::-moz-placeholder, & textarea::-moz-placeholder":
      placeholderResetSx,
    "& .MuiFormLabel-root, & .MuiInputLabel-root": {
      ...inputLabelDirectionSx,
      maxWidth: `calc(100% - ${OUTLINED_PADDING_INLINE * 2}px)`,
      lineHeight: "1.4375em",
    },
    "& .MuiInputLabel-root.MuiInputLabel-sizeSmall:not(.MuiInputLabel-shrink)": {
      transform: "translate(0, 9px) scale(1) !important",
    },
    "& .MuiOutlinedInput-root.MuiInputBase-multiline .MuiInputLabel-root:not(.MuiInputLabel-shrink)":
      {
        transform: `translate(0, ${OUTLINED_MULTILINE_PADDING_BLOCK}px) scale(1) !important`,
      },
    "& .MuiInputLabel-root.MuiInputLabel-sizeSmall.MuiInputLabel-shrink": {
      ...(isRTL
        ? { transform: "translate(0px, -9px) scale(0.75) !important",paddingRight: "6.5px !important" }
        : { transform: "translate(0px, -9px) scale(0.75) !important",paddingLeft: "6.5px !important" }),
    },
    "& .MuiOutlinedInput-notchedOutline legend": {
      textAlign: isRTL ? "right" : "left",
    },
    "& .MuiOutlinedInput-notchedOutline legend > span": {
      paddingInline: "5px",
    },
    "& .MuiAutocomplete-endAdornment": {
      top: "50%",
      transform: "translateY(-50%)",
      ...(isRTL
        ? { left: 9, right: "auto !important" }
        : { right: 9, left: "auto !important" }),
    },
  };
};

const getOutlinedFieldInputProps = (
  direction: OutlinedFieldDirection,
  inputProps?: InputHTMLAttributes<HTMLInputElement>
): InputHTMLAttributes<HTMLInputElement> => ({
  ...inputProps,
  dir: direction,
  style: {
    textAlign: direction === "rtl" ? "right" : "left",
    direction,
    lineHeight: 1.4375,
    ...inputProps?.style,
  },
});

const getOutlinedFieldInputLabelProps = (
  direction: OutlinedFieldDirection,
  inputLabelProps?: InputLabelProps
): InputLabelProps => ({
  ...inputLabelProps,
  dir: direction,
});

const outlinedBorderStyles = {
  borderWidth: "1px !important",
  borderStyle: "solid !important",
  borderColor:
    "var(--theme-outlined-field-border-color, rgba(0, 0, 0, 0.23)) !important",
};

const outlinedFieldSx: SxProps<Theme> = {
  width: "100%",
  "& .MuiOutlinedInput-root": {
    backgroundColor: "var(--theme-bg-primary, #FFFFFF)",
    "& fieldset, & .MuiOutlinedInput-notchedOutline": {
      ...outlinedBorderStyles,
    },
    "&:hover fieldset, &:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "var(--theme-primary-color, #2E3092) !important",
    },
    "&.Mui-focused fieldset, &.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "var(--theme-primary-color, #2E3092) !important",
    },
  },
  "& .MuiInputLabel-root": {
    color: "var(--theme-text-secondary, rgba(0, 0, 0, 0.6))",
    "&.Mui-focused": {
      color: "var(--theme-primary-color, #2E3092)",
    },
  },
};

const getOutlinedSingleLineRootSx = (
  inputHeight = OUTLINED_INPUT_HEIGHT,
  direction: OutlinedFieldDirection = "ltr"
) => ({
  backgroundColor: "var(--theme-bg-primary, #FFFFFF)",
  height: inputHeight,
  minHeight: inputHeight,
  boxSizing: "border-box" as const,
  direction,
  padding: "0 !important",
  "& fieldset, & .MuiOutlinedInput-notchedOutline": {
    ...outlinedBorderStyles,
  },
  "&:hover fieldset, &:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "var(--theme-primary-color, #2E3092) !important",
  },
  "&.Mui-focused fieldset, &.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "var(--theme-primary-color, #2E3092) !important",
  },
});

const getOutlinedAutocompleteSx = (
  inputHeight = OUTLINED_INPUT_HEIGHT,
  direction: OutlinedFieldDirection = "ltr"
): SxProps<Theme> => {
  const isRTL = direction === "rtl";
  const autocompleteInputPadding = isRTL
    ? {
        paddingRight: `${OUTLINED_PADDING_INLINE}px !important`,
        paddingLeft: `${
          OUTLINED_PADDING_INLINE + AUTOCOMPLETE_ICON_GUTTER
        }px !important`,
      }
    : {
        paddingLeft: `${OUTLINED_PADDING_INLINE}px !important`,
        paddingRight: `${
          OUTLINED_PADDING_INLINE + AUTOCOMPLETE_ICON_GUTTER
        }px !important`,
      };

  return {
    ...outlinedFieldSx,
    ...getOutlinedFieldDirectionSx(direction, true),
    "& .MuiOutlinedInput-root": getOutlinedSingleLineRootSx(inputHeight, direction),
    "& .MuiOutlinedInput-root.MuiAutocomplete-inputRoot": {
      padding: "0 !important",
      flexWrap: "nowrap",
      alignItems: "center",
    },
    "& .MuiOutlinedInput-root.MuiAutocomplete-inputRoot .MuiAutocomplete-input": {
      paddingTop: "0 !important",
      paddingBottom: "0 !important",
      alignSelf: "center",
      ...autocompleteInputPadding,
    },
    "& .MuiInputLabel-root": {
      color: "var(--theme-text-secondary, rgba(0, 0, 0, 0.6))",
      "&.Mui-focused": {
        color: "var(--theme-primary-color, #2E3092)",
      },
    },
  };
};

const getOutlinedSingleLineSx = (
  inputHeight = OUTLINED_INPUT_HEIGHT,
  direction: OutlinedFieldDirection = "ltr"
): SxProps<Theme> => ({
  ...outlinedFieldSx,
  ...getOutlinedFieldDirectionSx(direction, false),
  "& .MuiOutlinedInput-root": getOutlinedSingleLineRootSx(inputHeight, direction),
});

const getOutlinedMultilineSx = (
  direction: OutlinedFieldDirection = "ltr"
): SxProps<Theme> => ({
  ...getOutlinedFieldDirectionSx(direction, false),
  "& .MuiOutlinedInput-inputMultiline, & textarea.MuiOutlinedInput-input": {
    padding: "0 !important",
    lineHeight: 1.5,
    minHeight: "1.5em",
  },
});

export {
  GomakeOutlinedFieldClassName,
  getOutlinedAutocompleteSx,
  getOutlinedFieldDirectionSx,
  getOutlinedFieldInputLabelProps,
  getOutlinedFieldInputProps,
  getOutlinedMultilineSx,
  getOutlinedSingleLineSx,
  outlinedFieldSx,
  OUTLINED_INPUT_HEIGHT,
};
